/**
 * Auswahl der Bauartklasse als Kartenraster mit Wandaufbau-Schnitten.
 *
 * Vorher trug jede Karte eine kleine Haus-Silhouette (100x80 px). Die zeigte
 * ausgerechnet das, worin sich die Klassen NICHT unterscheiden: von außen sind
 * FHG I, FHG II und FHG III dasselbe Haus. Getrennt werden sie am Wandaufbau,
 * deshalb steht jetzt der Querschnitt auf der Karte (`bauartSchnitt.tsx`,
 * 200 px im Quadrat) und daneben zwei bis drei kurze Beschriftungen als echter
 * Text. Darunter unverändert Kürzel-Pille und Klartext.
 *
 * Props (`value`/`onChange`), die gespeicherten Werte und das Tastaturverhalten
 * sind unverändert.
 */
import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { BAUARTKLASSEN } from "./enums";
import { ILLU } from "./illustrationTokens";
import { BauartSchnitt, DachZoneIcon, schnittBeschriftungen } from "./bauartSchnitt";

/** Kantenlänge der Schnittzeichnung in der Karte — siehe Rechnung an `grid`. */
const SCHNITT_PX = 200;
/** Untergrenze einer Karte. Darunter bricht die Beschriftungsspalte um. */
const KARTE_MIN_PX = 300;

const useStyles = makeStyles({
  /**
   * Der Umbruch kommt aus der GEMESSENEN Rasterbreite, nicht aus dem Viewport.
   *
   * `auto-fit` + `minmax` ersetzt die bisherigen Viewport-Stufen (720/1000 px).
   * Der Grund ist derselbe, der schon das Inhaltsverzeichnis dieses Formulars
   * umgebaut hat (`risikoanalyseStep/useTocLayout.ts`): das Formular läuft auch
   * in einem schwebenden Fenster (Startbreite 1140 px, bis 420 px
   * herunterziehbar). Der Viewport bleibt dabei breit — die alte Regel
   * `@media (min-width: 1000px)` lieferte im geschrumpften Fenster vier Spalten
   * in einem ~350 px schmalen Inhaltsbereich, also ~80 px je Karte.
   *
   * Warum 300 px Untergrenze und keine feste Spaltenzahl: die Inhaltsspalte ist
   * auf 920 px gedeckelt (`pages/RisikoImmobilieStepForm.tsx`, Stil `inner`).
   * Mit dem beibehaltenen Abstand von 12 px ergäbe das je Karte
   *   4 Spalten → 221 px (abzüglich 2x12 px Polsterung 197 px innen)
   *   3 Spalten → 299 px
   *   2 Spalten → 454 px (abzüglich Polsterung 430 px innen)
   * Die Schnittzeichnung braucht 200 px; bei 197 px füllt sie die Karte
   * vollständig aus und für die Beschriftungen daneben bliebe nichts — vier
   * Spalten scheiden damit aus. Drei Spalten wären nur bei exakt voller
   * Inhaltsspalte erreichbar und fielen bei jeder Verschmälerung sofort auf
   * zwei zurück, kosteten also einen Umbruchpunkt ohne Ertrag. Bleiben zwei
   * Spalten als obere Stufe: 430 px innen = 200 px Zeichnung + 12 px Abstand +
   * 218 px für die Beschriftungen, ohne Umbruch. Faktisch entstehen so zwei
   * Stufen — eine Spalte unter 612 px Rasterbreite, zwei ab 612 px.
   *
   * `min(300px, 100%)` statt `300px` ist Absicht: unterschreitet der Container
   * die 300 px, liefe ein blankes `minmax(300px, 1fr)` über den Rand hinaus.
   */
  grid: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${KARTE_MIN_PX}px, 100%), 1fr))`,
    gap: "12px",
  },
  /**
   * `column-reverse`: im DOM steht ZUERST Kürzel + Klartext, gezeichnet wird die
   * Karte trotzdem Bild-oben. Grund ist der Vorlesename der Radio-Karte — er
   * entsteht aus ihrem Textinhalt, und dazu zählen auch die drei
   * Kurzbeschriftungen neben der Zeichnung. Stünde der Kopf auch im DOM zuerst,
   * begänne der Name mit „Ziegel, geschuppt …" statt mit der Klasse. Die
   * Zeichnung selbst steuert nichts bei (`dekorativ`, siehe unten). Die
   * Fokus-Reihenfolge bleibt unberührt: in der Karte liegt kein fokussierbares
   * Element.
   */
  card: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "left",
    padding: "0",
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "flex-end",
    alignItems: "stretch",
    overflow: "hidden",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, transform, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(45,20,43,0.08)",
    },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
  // Aktiv nur über Rahmen + Ring, bewusst ohne Flächentönung: die Tönung des
  // Illustrationskopfes würde den Aktiv-Kontrast oben schlucken.
  cardActive: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  /**
   * Zeichnung und Beschriftungen nebeneinander — mit `flexWrap` und ohne jede
   * Breitenabfrage. Unterschreitet die Karte die Summe aus Zeichnung und
   * Beschriftungsspalte, rutscht die Spalte selbsttätig darunter; die Zeichnung
   * schrumpft dabei nicht mit (`flexShrink: 0`).
   *
   * Kühl-neutraler Hintergrund statt des Flieders #f5eef6 — das ist im direkt
   * folgenden DachPicker die Hover-/Aktiv-Farbe (siehe ILLU.headerBg).
   */
  kopf: {
    backgroundColor: ILLU.headerBg,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "stretch",
    justifyContent: "center",
    columnGap: "12px",
    rowGap: "8px",
  },
  schnitt: {
    flexShrink: 0,
    alignSelf: "center",
    width: `min(${SCHNITT_PX}px, 100%)`,
    height: "auto",
    aspectRatio: "1 / 1",
  },
  /**
   * `flexBasis: 150px` ist die Schwelle, ab der die Spalte neben der Zeichnung
   * bleibt; `alignSelf: stretch` + `space-evenly` verteilt die drei Zeilen über
   * die Bildhöhe, sodass sie ungefähr auf Höhe der Zone stehen, die sie
   * benennen (Dach oben, Wand Mitte, Sockel unten). Nach dem Umbruch steht die
   * Spalte allein in ihrer Zeile — dort ist `space-evenly` wirkungslos.
   */
  beschriftungen: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    alignSelf: "stretch",
    flexGrow: 1,
    flexBasis: "150px",
    minWidth: 0,
    rowGap: "6px",
  },
  beschriftung: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    lineHeight: 1.3,
    color: BRAND.ink,
  },
  body: {
    paddingTop: "10px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    rowGap: "6px",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontAccent,
    fontSize: "0.85rem",
    fontWeight: 600,
    ...shorthands.borderRadius("999px"),
  },
  desc: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.86rem",
    lineHeight: 1.35,
    color: BRAND.ink,
  },
  // Steht ÜBER dem Raster, nicht in der Legende darunter: ohne die Leserichtung
  // sind die senkrechten Streifen im Bild nicht zu deuten, und diese Auskunft
  // braucht man beim Ansehen der Karten, nicht danach.
  leseHinweis: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.84rem",
    lineHeight: 1.4,
    color: BRAND.muted,
    marginTop: "0",
    marginBottom: "10px",
  },
  legende: {
    marginTop: "16px",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "18px",
    paddingRight: "18px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
  },
  legendeTitel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginBottom: "10px",
  },
  legendeReihe: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    marginBottom: "8px",
  },
  // Ohne flexShrink schrumpft das 64x21-Icon auf 360 px neben dem längeren
  // Legendentext anteilig mit und wird unlesbar.
  legendeIcon: {
    flexShrink: 0,
    display: "flex",
  },
  legendeText: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.86rem",
    color: BRAND.ink,
  },
  legendeStark: {
    fontWeight: 600,
  },
  legendeFuss: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
    marginTop: "10px",
  },
});

type Props = {
  value: string;
  onChange: (next: string) => void;  /** Eindeutige Beschriftung, wenn mehrere Auswahlen auf einer Seite stehen (Anbauten). */
  ariaLabel?: string;
};

const BauartklassePicker = ({ value, onChange, ariaLabel = "Bauartklasse" }: Props) => {
  const styles = useStyles();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  // Roving Tabindex: die gewählte Karte ist tabbar. Bei leerem oder unbekanntem
  // Wert (z. B. aus dem Legacy-Formular) fällt der Fokus auf die erste Karte —
  // sonst wäre die Gruppe per Tastatur gar nicht erreichbar.
  const gewaehlt = BAUARTKLASSEN.findIndex((b) => b.value === value);
  const tabIndexOf = (i: number) => (i === (gewaehlt >= 0 ? gewaehlt : 0) ? 0 : -1);

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const letzter = BAUARTKLASSEN.length - 1;
    let ziel = -1;
    let pfeil = false;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      ziel = i === letzter ? 0 : i + 1;
      pfeil = true;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      ziel = i === 0 ? letzter : i - 1;
      pfeil = true;
    } else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = letzter;
    if (ziel < 0) return;
    e.preventDefault();
    // Ohne Vorauswahl steht der Fokus auf Karte 0, gewählt ist aber nichts.
    // Ein Weiterwandern würde BAK I stillschweigend überspringen — in einer
    // Radiogroup ohne Vorauswahl aktiviert der erste Pfeildruck deshalb die
    // fokussierte Karte. Home/End bleiben absolute Sprünge.
    const naechste = pfeil && gewaehlt < 0 ? i : ziel;
    refs.current[naechste]?.focus();
    onChange(BAUARTKLASSEN[naechste].value);
  };

  return (
    <div>
      <p className={styles.leseHinweis}>
        Die Bilder zeigen die Wand im Querschnitt — außen links, innen rechts, darüber die Dachung, darunter der Sockel.
      </p>

      <div className={styles.grid} role="radiogroup" aria-label={ariaLabel}>
        {BAUARTKLASSEN.map((b, i) => {
          const active = value === b.value;
          return (
            <button
              type="button"
              key={b.value}
              ref={(el) => {
                refs.current[i] = el;
              }}
              role="radio"
              aria-checked={active}
              tabIndex={tabIndexOf(i)}
              className={mergeClasses(styles.card, active && styles.cardActive)}
              onClick={() => onChange(b.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            >
              {/* Kürzel und Klartext stehen im DOM zuerst, gezeichnet werden sie
                  unten — siehe `column-reverse` am Stil `card`. */}
              <span className={styles.body}>
                <span className={styles.pill}>{b.value}</span>
                <span className={styles.desc}>{b.kurz}</span>
              </span>
              <span className={styles.kopf}>
                {/* `dekorativ`: die Zeichnung sagt nichts, was nicht direkt
                    daneben als Text steht — im Vorlesenamen der Karte wäre ihre
                    Beschreibung eine Dublette von ~200 Zeichen, bei jedem
                    Pfeildruck neu. Siehe Prop-Kommentar in `bauartSchnitt.tsx`. */}
                <BauartSchnitt wand={b.wand} dach={b.dach} className={styles.schnitt} dekorativ />
                <span className={styles.beschriftungen}>
                  {schnittBeschriftungen(b.wand, b.dach).map((zeile) => (
                    <span key={zeile} className={styles.beschriftung}>
                      {zeile}
                    </span>
                  ))}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* „Weiche Dachung" ist für Laien der eigentliche Stolperstein — deshalb
          die Legende mit Beispielen statt nur mit dem Fachbegriff. Die Icons
          zeigen dieselbe Dachzone wie die Karten, nur klein. */}
      <div className={styles.legende}>
        <div className={styles.legendeTitel}>Harte oder weiche Dachung?</div>
        <div className={styles.legendeReihe}>
          <span className={styles.legendeIcon}>
            <DachZoneIcon dach="hart" />
          </span>
          <span className={styles.legendeText}>
            <span className={styles.legendeStark}>Harte Dachung</span> — Ziegel, Beton, Schiefer, Metall
          </span>
        </div>
        <div className={styles.legendeReihe}>
          <span className={styles.legendeIcon}>
            <DachZoneIcon dach="weich" />
          </span>
          <span className={styles.legendeText}>
            <span className={styles.legendeStark}>Weiche Dachung</span> — Reet, Stroh, Holzschindeln
          </span>
        </div>
        <div className={styles.legendeFuss}>
          Abweichende Einstufungen sind je nach Versicherer und Tarif möglich — im Zweifel gilt die Angabe aus dem
          Vorvertrag.
        </div>
      </div>
    </div>
  );
};

export default BauartklassePicker;
